import React from 'react';
import {IContextualMenuItem, Panel, PanelType, Stack, useTheme} from '@fluentui/react';
import OpenInCrmMenu from './OpenInCrmMenu';
import {IRecordLink} from '../../store/endpoints/RecordLinksEndpoints';

interface IStandardPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    /** Panel title, shown left in the header. */
    headerText?: string;
    type?: PanelType;
    /** false = no overlay and the page stays interactive (dock beside content); default blocks like Fluent. */
    isBlocking?: boolean;
    /** CRM/PSA record links — rendered as "Open in {folio}" in the header three-dots menu. */
    links?: IRecordLink[];
    /** Extra header three-dots actions (e.g. "Link with {CRM}", "Sync all …"). */
    menuItems?: IContextualMenuItem[];
    isFooterAtBottom?: boolean;
    onRenderFooterContent?: () => React.ReactNode;
    children?: React.ReactNode;
}

/**
 * A Fluent Panel with the standard Adaptive Catalog header: title on the left and a vertical three-dots
 * overflow menu on the right (matching the procurement panels) that combines "Open in {CRM}" links with any
 * extra actions. Use this instead of hand-rolling the header + <see cref="OpenInCrmMenu"/> in every editor.
 */
const StandardPanel: React.FC<IStandardPanelProps> = (props) => {
    const theme = useTheme();
    return (
    <Panel
        type={props.type ?? PanelType.medium}
        isBlocking={props.isBlocking}
        isOpen={props.isOpen}
        onDismiss={props.onDismiss}
        headerText={props.headerText}
        closeButtonAriaLabel='Close'
        isFooterAtBottom={props.isFooterAtBottom}
        onRenderFooterContent={props.onRenderFooterContent as (() => JSX.Element) | undefined}
        // A pinned footer floats over the scrolling body, and Fluent leaves it transparent — so the
        // buttons appear to hover over the content, and the footer's own padding stays see-through even
        // if the caller paints its buttons. Painting the footer SLOT covers the whole region, padding
        // included, and fixes it for every panel built on this rather than one caller at a time.
        styles={{
            footer: {
                backgroundColor: theme.palette.white,
                borderTop: `1px solid ${theme.palette.neutralLight}`,
            },
        }}
        onRenderHeader={() => (
            <Stack horizontal horizontalAlign='space-between' verticalAlign='center' styles={{root: {width: '100%', padding: '0 24px'}}}>
                <h1 style={{margin: 0, fontSize: 20}}>{props.headerText}</h1>
                <OpenInCrmMenu links={props.links} extraItems={props.menuItems} />
            </Stack>
        )}
    >
        {props.children}
    </Panel>
    );
};

export default StandardPanel;
